// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * fix-sprint-20260912-1003 — how a Framework Stack row is PRESENTED.
 *
 * ## Why this is in `utils/` and not `types/`
 *
 * `FrameworkStackLayerClassification.kind` is a render-time projection: it is
 * computed from a row's stored `status` every time the rail draws, and no
 * database, route or pack ever holds one. It first lived in
 * `types/localization.ts` beside the stored vocabulary, and
 * `union-reachability-census` correctly reported all seven of its members as
 * written by no route-reachable path — because nothing writes them at all.
 *
 * That census's own scope note already names this shape: *"Fields with no writer
 * anywhere in backend non-test code. These are response and projection shapes
 * rather than state machines."* Its exclusion keys on the writer's location, and
 * this one's writer (`classifyStackLayer`) sits in `shared`, so the exclusion did
 * not apply. The fix is not an exemption — none of the five permitted reasons
 * (`system_raised`, `initial_value`, `migration_only`, `seed_only`,
 * `sweep_only`) describes a derived value, and claiming one would be the
 * allowlist-wearing-a-justification that census exists to abolish. The fix is
 * that a projection does not belong in the directory of persisted vocabularies.
 *
 * The STORED half stays in `types/localization.ts`, where the census governs it:
 * `FRAMEWORK_STACK_ROW_STATUSES`, `FrameworkStackLayer` and
 * `stackRowStatusToRecordStatus`.
 */
/** The presentation state the stack rail and its readers render. */
import { frameworkDutiesAreOwed } from '../types/localization.js';
/**
 * The one classifier every stack-row reader goes through.
 *
 * Before this, the rail asked `layer.status === 'active'` raw and answered the
 * badge with a ternary, so `upcoming` rendered as "Draft" and the three states that
 * mattered most rendered as nothing. Readers must not re-derive this: a second
 * answer to "what is this row" is how the badge and the link came to disagree about
 * `customer_facing_framework`.
 *
 * An unrecognised status returns `kind: 'unknown'` and is **not** treated as
 * lineage — it stays visible. Hiding a row nobody has classified would remove an
 * instrument from the page on the strength of not understanding it, which is the
 * opposite of what the caller needs; `normalizeFrameworkStatus` takes the same
 * stance by returning `null` rather than guessing a bucket.
 */
export function classifyStackLayer(layer) {
    const status = layer.status ?? '';
    switch (status) {
        case 'active':
            return {
                kind: 'in_force',
                labelKey: 'stack_status_in_force',
                dateForLabel: null,
                isLineage: false,
                isLinkable: true,
            };
        case 'active_reference':
            return {
                kind: 'in_force',
                labelKey: 'stack_status_in_force_reference',
                dateForLabel: null,
                isLineage: false,
                isLinkable: true,
            };
        case 'temporary_active':
            // Sprint 63.40.0 — "In force to {date}" names the LAST day in force,
            // `effective_until`. It used to be filled from `effective_date`, the first
            // day, so the first temporary row the corpus carried would have read "In
            // force to 1 July 2025" for an instrument in force until 28 February 2027.
            // A row that states no last day reads "In force", never a wrong date.
            return layer.effective_until
                ? {
                    kind: 'in_force',
                    labelKey: 'stack_status_in_force_until',
                    dateForLabel: layer.effective_until,
                    isLineage: false,
                    isLinkable: true,
                }
                : {
                    kind: 'in_force',
                    labelKey: 'stack_status_in_force',
                    dateForLabel: null,
                    isLineage: false,
                    isLinkable: true,
                };
        case 'upcoming':
            return {
                kind: 'upcoming',
                labelKey: 'stack_status_from_date',
                dateForLabel: layer.effective_date ?? null,
                isLineage: false,
                isLinkable: true,
            };
        case 'draft':
            return {
                kind: 'draft',
                labelKey: 'stack_status_draft',
                dateForLabel: null,
                isLineage: false,
                isLinkable: true,
            };
        case 'promulgated':
            return {
                kind: 'promulgated',
                labelKey: 'stack_status_promulgated',
                dateForLabel: layer.lineage?.on ?? null,
                isLineage: true,
                isLinkable: true,
            };
        case 'superseded':
            return {
                kind: 'superseded',
                labelKey: 'stack_status_superseded',
                dateForLabel: layer.lineage?.on ?? null,
                isLineage: true,
                isLinkable: true,
            };
        case 'official_roadmap':
            return {
                kind: 'reference',
                labelKey: 'stack_status_reference',
                dateForLabel: null,
                isLineage: false,
                isLinkable: false,
            };
        default:
            return {
                kind: 'unknown',
                labelKey: 'stack_status_unknown',
                dateForLabel: null,
                isLineage: false,
                isLinkable: true,
            };
    }
}
/**
 * Present `child_frameworks` rows as stack rows, so one renderer draws both.
 *
 * ## fix-sprint-20260912-1003 F13 — the defect
 *
 * `child_frameworks` carries the same statuses as `framework_stack` and had the
 * same defects — stale `upcoming` on commenced instruments, `superseded` where
 * `promulgated` was meant, loose `superseded_by_instrument_code` siblings that
 * nothing read. Its data was corrected alongside the stack, and it still had
 * **no renderer**. Four records declare children and no `framework_stack`, so
 * 14 child instruments appeared on no page:
 *
 *   VN_ELECTRONIC_TRANSACTIONS_LAW_2023                  4
 *   VN_TRUST_SERVICES_TECHNICAL_STANDARDS_CLUSTER_2025   7
 *   VN_IDENTITY_LAW_2023                                 2
 *   VN_ELECTRONIC_LABOR_CONTRACTS_DECREE_337_2025        1
 *
 * ## Why a converter and not a second component
 *
 * A parallel renderer is how these two shapes came to carry the same defects
 * independently. Converting means the badge vocabulary, the lineage fold, the
 * linkability rule and the focus behaviour are decided in exactly one place —
 * so a fix to one is a fix to both, by construction rather than by discipline.
 *
 * ## What is NOT derived
 *
 * `relationship` is an open 24-value vocabulary and is not mapped onto
 * `FrameworkStackLayerType`; every derived row is `child_instrument` and keeps
 * its relationship text. Deriving a layer per relationship would assert
 * legislative classifications no pack made, and produce 24 one-row groups.
 *
 * `resolveName` is supplied by the caller because the name lives on the
 * framework record the code names, not on the child row. When it returns
 * nothing the code itself is the name — a visible instrument with an ugly label
 * beats a missing one.
 */
export function childFrameworksToStackLayers(children, resolveName) {
    if (!Array.isArray(children))
        return [];
    const seen = new Set();
    const out = [];
    for (const child of children) {
        const code = child?.child_framework_code;
        if (typeof code !== 'string' || code.length === 0)
            continue;
        // A duplicate code would render the same instrument twice, which reads as
        // two separate legal steps — the same rule findFrameworkStackErrors applies
        // to `framework_stack`.
        if (seen.has(code))
            continue;
        seen.add(code);
        const base = {
            layer_type: 'child_instrument',
            instrument_code: code,
            instrument_name: resolveName?.(code) || code,
            effective_date: child.effective_date ?? null,
            pack_code: child.pack_code ?? null,
            customer_facing_framework: child.customer_facing_framework,
            retained_for: child.retained_for,
            relationship: child.relationship,
        };
        // The status/lineage pairing is the same discriminated union the stack uses,
        // so a lineage row with no successor cannot be produced here either.
        if ((child.status === 'promulgated' || child.status === 'superseded') &&
            child.lineage?.successor_instrument_code &&
            child.lineage?.on) {
            out.push({ ...base, status: child.status, lineage: child.lineage });
            continue;
        }
        out.push({
            ...base,
            status: child.status,
        });
    }
    return out;
}
/**
 * Sprint 62.27.0 (LD3) — the instrument codes whose declarations are owed by
 * an org's installed frameworks, parent and children alike.
 *
 * The owner's ruling that opened this sprint made the reach explicit:
 * *"just making the comment to ensure you include child frameworks for law 91"*.
 * Reading only each installed `RegulatoryFramework.code` answers a smaller
 * question than the one asked — on this tree `VN_PDPL_LAW_2025` alone would
 * return one instrument where the stack declares four.
 *
 * Three rules, in order:
 *
 * 1. Every installed framework's own `code`.
 * 2. Its `framework_stack[]` rows, and its `child_frameworks[]` rows **through
 *    `childFrameworksToStackLayers`** rather than by a second walk. The two
 *    shapes came to carry the same defects independently precisely because
 *    each had its own reader (the F13 ruling), and a parallel walk here would
 *    be the third.
 * 3. Drop every row `classifyStackLayer` reports as `isLineage` — a
 *    `superseded` or `promulgated` row with a successor — and every row whose
 *    `frameworkDutiesAreOwed(status)` is false.
 *
 * **A row this cannot classify is kept, not dropped.** `classifyStackLayer`
 * returns `kind: 'unknown'` with `isLineage: false` for a status nobody has
 * seen, and `frameworkDutiesAreOwed` counts an unknown status. Both stances are
 * deliberate and this function inherits rather than overrides them: an
 * instrument that survives here but declares nothing is reported by the reader
 * as `declaration_not_verified` (LD5), which is a visible unanswered question.
 * Dropping it here would instead make it silently contribute nothing, which is
 * indistinguishable from a verified negative and is the error LD5 exists to
 * prevent.
 *
 * Returns codes in first-seen order with duplicates removed: an instrument
 * reachable as both a stack row and a child row is one instrument.
 */
export function expandInstalledStack(frameworks) {
    const out = [];
    const seen = new Set();
    const take = (code, status) => {
        if (typeof code !== 'string')
            return;
        const trimmed = code.trim();
        if (trimmed.length === 0 || seen.has(trimmed))
            return;
        if (!frameworkDutiesAreOwed(status))
            return;
        seen.add(trimmed);
        out.push(trimmed);
    };
    for (const framework of frameworks ?? []) {
        if (!framework)
            continue;
        take(framework.code, framework.status);
        const rows = [
            ...(Array.isArray(framework.framework_stack) ? framework.framework_stack : []),
            ...childFrameworksToStackLayers(framework.child_frameworks),
        ];
        for (const row of rows) {
            if (!row)
                continue;
            if (classifyStackLayer(row).isLineage)
                continue;
            take(row.instrument_code, row.status);
        }
    }
    return out;
}
//# sourceMappingURL=framework-stack-presentation.js.map